import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useContext } from "react";
import { Button, MenuItem, Stack, TextField } from "@mui/material";
import { useParams } from "react-router-dom";

import { generateCoreNetworkMatches, listCoreNetworkMatches, listCoreNetworkWorkRequests, sendCoreNetworkOffers, shortlistCoreNetworkMatches } from "../../../services/coreNetwork";
import { AuthContext } from "../../../helpers/AuthContext";
import { CORE_NETWORK_PERMISSIONS, hasAnyCoreNetworkPermission, hasCoreNetworkPermission } from "../../../helpers/coreNetworkPermissions";
import {
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  LoadingState,
  SectionCard,
  safeArray,
} from "./coreNetworkUiShared";
import CoreNetworkMatchPanel from "./components/CoreNetworkMatchPanel";

export default function CoreNetworkMatchesPage() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [workRequests, setWorkRequests] = useState([]);
  const [selectedWorkRequestId, setSelectedWorkRequestId] = useState("");
  const [matches, setMatches] = useState([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const canManagePayment = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_PAYMENT, instanceUniqueId);
  const canViewPlacement = hasAnyCoreNetworkPermission(
    authState,
    [CORE_NETWORK_PERMISSIONS.MANAGE_BADGES, CORE_NETWORK_PERMISSIONS.MANAGE_SUBSCRIPTIONS],
    instanceUniqueId
  );
  const canGenerateMatches = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.GENERATE_MATCHES, instanceUniqueId);
  const canSendOffers = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.SEND_OFFERS, instanceUniqueId);
  const canShortlist = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.ASSIGN_REQUEST, instanceUniqueId);

  const loadMatchesPage = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const workResponse = await listCoreNetworkWorkRequests({ instanceUniqueId });
      const nextWorkRequests = safeArray(workResponse.workRequests);
      setWorkRequests(nextWorkRequests);
      const nextSelected = selectedWorkRequestId || nextWorkRequests[0]?.id || "";
      setSelectedWorkRequestId(String(nextSelected || ""));
      if (nextSelected) {
        const matchesResponse = await listCoreNetworkMatches(nextSelected, { instanceUniqueId });
        setMatches(safeArray(matchesResponse.matches));
      } else {
        setMatches([]);
      }
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError.message || "Unable to load matches.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId, selectedWorkRequestId]);

  useEffect(() => {
    loadMatchesPage();
  }, [loadMatchesPage]);

  const selectedWorkRequest = useMemo(
    () => workRequests.find((request) => String(request.id) === String(selectedWorkRequestId)) || null,
    [selectedWorkRequestId, workRequests]
  );

  const reloadMatchesOnly = useCallback(async (workRequestIdValue) => {
    if (!workRequestIdValue) {
      setMatches([]);
      return;
    }
    const matchesResponse = await listCoreNetworkMatches(workRequestIdValue, { instanceUniqueId });
    setMatches(safeArray(matchesResponse.matches));
  }, [instanceUniqueId]);

  const runAction = async (actionKey, callback) => {
    try {
      setBusyAction(actionKey);
      await callback();
      await reloadMatchesOnly(selectedWorkRequestId);
    } catch (actionError) {
      setError(actionError?.response?.data?.message || actionError.message || "Match action failed.");
    } finally {
      setBusyAction("");
    }
  };

  return (
    <CoreNetworkShell
      title="Core Network Matches"
      breadcrumb={`${CORE_NETWORK_LABEL} / Matches`}
    >
      {loading ? <LoadingState label="Loading Core Network matches..." /> : null}
      {!loading && error ? <ErrorState message={error} onAction={loadMatchesPage} /> : null}
      {!loading && !error ? (
        <Stack spacing={3}>
          <SectionCard
            title="Match Explorer"
            subtitle="Work-request-centric view of current scored provider recommendations."
            action={<Button variant="outlined" onClick={loadMatchesPage}>Refresh</Button>}
          >
            <TextField
              select
              fullWidth
              label="Work request"
              value={selectedWorkRequestId}
              onChange={async (event) => {
                const nextValue = event.target.value;
                setSelectedWorkRequestId(nextValue);
                setSelectedMatchIds([]);
                try {
                  setLoading(true);
                  await reloadMatchesOnly(nextValue);
                } finally {
                  setLoading(false);
                }
              }}
              sx={{ mb: 3 }}
            >
              {workRequests.map((request) => (
                <MenuItem key={request.id} value={request.id}>
                  {request.title || `Work Request #${request.id}`}
                </MenuItem>
              ))}
            </TextField>

            <CoreNetworkMatchPanel
              matches={matches}
              selectedMatchIds={selectedMatchIds}
              onToggleMatch={(matchId) =>
                setSelectedMatchIds((prev) =>
                  prev.includes(matchId) ? prev.filter((value) => value !== matchId) : [...prev, matchId]
                )
              }
              onGenerateMatches={
                selectedWorkRequest && canGenerateMatches
                  ? () => runAction("generate", () => generateCoreNetworkMatches(selectedWorkRequest.id, { instanceUniqueId, regenerate: true }))
                  : null
              }
              onShortlistMatches={
                selectedWorkRequest && canShortlist
                  ? () => runAction("shortlist", () => shortlistCoreNetworkMatches(selectedWorkRequest.id, { instanceUniqueId, matchIds: selectedMatchIds }))
                  : null
              }
              onSendOffers={
                selectedWorkRequest && canSendOffers
                  ? () => runAction("offers", () => sendCoreNetworkOffers(selectedWorkRequest.id, { instanceUniqueId, limit: Math.max(selectedMatchIds.length, 3) }))
                  : null
              }
              busyAction={busyAction}
              canViewPayment={canManagePayment}
              canViewPlacement={canViewPlacement}
            />
          </SectionCard>
        </Stack>
      ) : null}
    </CoreNetworkShell>
  );
}
